import { AnimatePresence, motion } from "framer-motion";
import { Flame, X, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { openStripeLink } from "../lib/stripe";

export default function PackPreviewModal({ open, pack, onClose }) {
  return (
    <AnimatePresence>
      {open && pack && (
        <motion.div
          className="fixed inset-0 z-[62] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="pack-preview-overlay"
          onClick={onClose}
        >
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background: "rgba(5,3,14,0.72)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            }}
          />
          <motion.div
            initial={{ y: "100%", opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: "100%", opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-full sm:max-w-sm mx-0 sm:mx-4"
            data-testid="pack-preview-sheet"
          >
            <Body pack={pack} onClose={onClose} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Body({ pack, onClose }) {
  const { user } = useAuth();
  const { catalog } = useBilling();
  const [busy, setBusy] = useState(false);
  const preview = pack?.preview || [];

  const isSpicy = pack?.id === "spicy-mode";
  const introLink = catalog?.intro?.payment_link;
  const bundleLink = catalog?.bundle?.payment_link;
  const introPrice = catalog?.intro?.amount?.toFixed(2) ?? "0.99";
  const bundlePrice = catalog?.bundle?.amount?.toFixed(2) ?? "4.99";

  const headline = isSpicy ? "Turn up the heat 🔥" : `Unlock ${pack.name} ${pack.emoji}`;
  // For non-spicy paid packs, only the bundle unlocks them.
  const ctaLabel = isSpicy ? `Unlock for $${introPrice}` : `Unlock everything · $${bundlePrice}`;
  const ctaLink = isSpicy ? introLink : bundleLink;
  const sub = isSpicy
    ? `Or unlock everything for $${bundlePrice}`
    : `One-time · all paid packs forever`;

  const claim = async () => {
    if (!ctaLink) {
      toast.error("Checkout link unavailable. Try again in a moment.");
      return;
    }
    setBusy(true);
    const ok = await openStripeLink(ctaLink, { user });
    setBusy(false);
    if (ok) {
      toast.success("Opened Stripe in a new tab — finish your purchase 💫");
      onClose();
    } else {
      toast.error("Couldn't open the checkout. Pop-up blocker?");
    }
  };

  const claimBundle = async () => {
    if (!bundleLink) {
      toast.error("Checkout link unavailable.");
      return;
    }
    setBusy(true);
    const ok = await openStripeLink(bundleLink, { user });
    setBusy(false);
    if (ok) {
      toast.success("Opened Stripe in a new tab — finish your purchase 💫");
      onClose();
    } else {
      toast.error("Couldn't open the checkout.");
    }
  };

  return (
    <div
      className="relative overflow-hidden bg-[#0F0F14]/95 border border-white/10 sm:rounded-3xl rounded-t-3xl p-6 text-white"
      style={{ boxShadow: "0 0 60px rgba(255,77,141,0.35)" }}
    >
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,77,141,0.16) 50%, transparent 100%)",
          backgroundSize: "45% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition z-10"
        data-testid="pack-preview-close"
        aria-label="Close preview"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative">
        <div className="flex items-center gap-3 mb-5">
          <motion.span
            className="text-4xl"
            animate={{ rotate: [0, 10, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            {pack.emoji}
          </motion.span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF4D8D] mb-0.5">Preview</p>
            <h3 className="font-display text-2xl font-bold leading-tight">{headline}</h3>
          </div>
        </div>

        <p className="text-xs text-[#C9C7D9] mb-3 italic">"{pack.description}"</p>

        <ul className="space-y-2 mb-5">
          {preview.slice(0, 3).map((q, i) => {
            const isLast = i === 2;
            return (
              <li
                key={i}
                className="relative rounded-xl p-3 bg-white/[0.03] border border-white/10 text-sm text-white leading-snug"
                data-testid={`pack-preview-item-${i}`}
              >
                <span
                  className="inline-block w-6 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF4D8D] mr-2"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span style={isLast ? { filter: "blur(5px)", userSelect: "none" } : undefined}>
                  {q}
                </span>
                {isLast && (
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF4D8D] bg-black/50 px-2.5 py-1 rounded-full">
                      <Lock className="w-3 h-3" /> Unlock to reveal
                    </span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {pack.hook && (
          <p className="text-center text-xs text-[#C9C7D9] mb-4 italic">"{pack.hook}"</p>
        )}

        <motion.button
          onClick={claim}
          disabled={busy}
          whileTap={{ scale: 0.96 }}
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="relative overflow-hidden w-full btn-gradient rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="pack-preview-cta"
        >
          <motion.span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            animate={{ backgroundPositionX: ["0%", "200%"] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.32) 50%, transparent 100%)",
              backgroundSize: "40% 100%",
              backgroundRepeat: "no-repeat",
            }}
          />
          <span className="relative inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
            {busy ? "Opening…" : ctaLabel}
          </span>
        </motion.button>
        {isSpicy && (
          <button
            type="button"
            onClick={claimBundle}
            disabled={busy}
            className="mt-3 w-full rounded-full py-2.5 text-xs font-bold text-white/80 hover:text-white bg-white/[0.03] border border-white/10 hover:border-white/25 transition inline-flex items-center justify-center gap-2"
            data-testid="pack-preview-bundle"
          >
            <span>Or</span>
            <span className="text-[#FF4D8D]">unlock everything 😈</span>
            <span className="font-display-tight font-bold">${bundlePrice}</span>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] bg-[#FF4D8D]/15 text-[#FF4D8D] border border-[#FF4D8D]/40 px-1.5 py-0.5 rounded-full">Best Value</span>
          </button>
        )}
        <p className="text-[9px] text-center uppercase tracking-[0.3em] text-[#C9C7D9]/70 mt-3 font-semibold">
          {sub} · Secure checkout by Stripe
        </p>
      </div>
    </div>
  );
}
